import {testIdProps} from '@github-ui/test-id-props'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type RefObject, startTransition, useCallback, useRef, useState} from 'react'

import {MergeabilitySidesheet} from './MergeabilitySidesheet'
import {presentationForStatus, Status, mergeabilityStatus} from '../helpers/mergeability-status'
import {useMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'
import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import type {MergeBoxPageData} from '../page-data/payloads/merge-box'
import {useMergeabilityLiveUpdates, type Channels} from '../hooks/use-mergeability-live-updates'
import {useStatusChecksPageDataWithoutError} from '../page-data/loaders/use-status-checks-page-data'
import type {StatusChecksPageData} from '../page-data/payloads/status-checks'
import styles from './MergeStatusButton.module.css'

interface MergeStatusButtonCommonProps {
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  toggleMergeabilitySidesheet: (isOpen: boolean) => void
}

/**
 * A button that displays the merge status of a pull request.
 * Opens the mergeability sidesheet.
 */
export const MergeStatusButtonWrapper = memo(function MergeStatusButtonWrapper({
  channels,
  headSha,
  helpUrl,
  viewerLogin,
}: {
  channels: Channels
  headSha: string
  helpUrl: string
  viewerLogin: string
}) {
  const {mergeMethod} = useMergeMethodContext()
  const {data, refetch} = useMergeBoxPageData({mergeMethod, bypassRequirements: false})
  const {data: statusChecksData} = useStatusChecksPageDataWithoutError({
    pullRequestHeadSha: headSha,
  })

  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const [mergeabilitySidesheetIsOpen, setMergeabilitySidesheetIsOpen] = useState(false)
  const toggleMergeabilitySidesheet = useCallback((isOpen: boolean) => {
    setMergeabilitySidesheetIsOpen(isOpen)
  }, [])

  const sidesheet = (
    <MergeabilitySidesheet
      helpUrl={helpUrl}
      mergeStatusButtonRef={mergeStatusButtonRef}
      mergeabilitySidesheetIsOpen={mergeabilitySidesheetIsOpen}
      toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
      viewerLogin={viewerLogin}
      channels={channels}
    />
  )

  const refetchQuery = useCallback(() => {
    startTransition(() => {
      refetch({cancelRefetch: false})
    })
  }, [refetch])

  useMergeabilityLiveUpdates({refetchQuery, channels})

  const isLoading = !data || !statusChecksData

  return (
    <>
      <MergeStatusButton
        isLoading={isLoading}
        pullRequest={data?.pullRequest}
        mergeRequirements={data?.mergeRequirements}
        statusChecksData={statusChecksData}
        mergeStatusButtonRef={mergeStatusButtonRef}
        toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
      />
      {sidesheet}
    </>
  )
})

export type MergeStatusButtonData = MergeBoxPageData

interface MergeStatusButtonProps extends MergeStatusButtonCommonProps {
  isLoading?: boolean
  pullRequest?: MergeBoxPageData['pullRequest']
  mergeRequirements?: MergeBoxPageData['mergeRequirements']
  statusChecksData?: StatusChecksPageData
}

export function MergeStatusButton({
  isLoading,
  pullRequest,
  mergeRequirements,
  statusChecksData,
  mergeStatusButtonRef,
  toggleMergeabilitySidesheet,
}: MergeStatusButtonProps) {
  if (!mergeStatusButtonRef) return null

  const status =
    !isLoading && pullRequest
      ? mergeabilityStatus({pullRequest, mergeRequirements: mergeRequirements ?? null, statusChecksData})
      : null

  // Don't render merge status button if failure is non-actionable (repo is not writeable, etc.)
  if (status === Status.NonactionableFailure) return null

  const presentation = status ? presentationForStatus(status) : null

  return (
    <Button
      ref={mergeStatusButtonRef}
      leadingVisual={presentation?.icon}
      variant="default"
      {...testIdProps('merge-status-button')}
      className={clsx(styles.mergeStatusButton, presentation && styles[presentation.iconColorClass])}
      onClick={() => toggleMergeabilitySidesheet?.(true)}
      size="medium"
      loading={isLoading}
      inactive={isLoading}
      aria-label={isLoading ? 'Loading merge status' : undefined}
    >
      <span className="d-flex hide-lg hide-xl">View status</span>
      <span className="hide-sm hide-md d-lg-flex">{presentation?.title}</span>
    </Button>
  )
}
